import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type FieldType = "string" | "number" | "boolean" | "array";

interface ExtractionField {
  id: string;
  key: string;
  label: string;
  description: string;
  type: FieldType;
  required: boolean;
}

function defaultField(): ExtractionField {
  return {
    id: crypto.randomUUID(),
    key: "",
    label: "",
    description: "",
    type: "string",
    required: false,
  };
}

function parseFieldType(value: string): FieldType {
  if (value === "number" || value === "boolean" || value === "array") {
    return value;
  }
  return "string";
}

function parseFields(raw: string): ExtractionField[] {
  if (raw.trim().length === 0) {
    return [defaultField()];
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (
      parsed === null ||
      typeof parsed !== "object" ||
      !("properties" in parsed) ||
      parsed.properties === null ||
      typeof parsed.properties !== "object"
    ) {
      return [defaultField()];
    }

    const root = parsed as {
      properties: Record<string, unknown>;
      required?: unknown;
    };

    const requiredSet = new Set(
      Array.isArray(root.required)
        ? root.required.filter(
            (item): item is string => typeof item === "string",
          )
        : [],
    );

    const fields = Object.entries(root.properties).map(([key, value]) => {
      const base = defaultField();
      base.key = key;
      base.label = key;
      base.required = requiredSet.has(key);

      if (value !== null && typeof value === "object") {
        const prop = value as Record<string, unknown>;
        base.label = typeof prop.title === "string" ? prop.title : key;
        base.description =
          typeof prop.description === "string" ? prop.description : "";
        base.type = parseFieldType(String(prop.type ?? "string"));
      }

      return base;
    });

    return fields.length > 0 ? fields : [defaultField()];
  } catch {
    return [defaultField()];
  }
}

function buildSchema(fields: ExtractionField[]): string {
  const valid = fields
    .map((f) => ({
      ...f,
      key: f.key.trim(),
      label: f.label.trim(),
      description: f.description.trim(),
    }))
    .filter((f) => f.key.length > 0);

  if (valid.length === 0) {
    return "";
  }

  const properties: Record<string, Record<string, unknown>> = {};
  const required: string[] = [];

  for (const field of valid) {
    const prop: Record<string, unknown> = {
      title: field.label.length > 0 ? field.label : field.key,
    };

    if (field.description.length > 0) {
      prop.description = field.description;
    }

    if (field.type === "array") {
      prop.type = "array";
      prop.items = { type: "string" };
    } else {
      prop.type = field.type;
    }

    properties[field.key] = prop;

    if (field.required) {
      required.push(field.key);
    }
  }

  const schema: Record<string, unknown> = {
    type: "object",
    properties,
    additionalProperties: false,
  };

  if (required.length > 0) {
    schema.required = required;
  }

  return JSON.stringify(schema, null, 2);
}

interface ExtractionSchemaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onApply: (schema: string) => void;
}

export function ExtractionSchemaDialog({
  open,
  onOpenChange,
  value,
  onApply,
}: ExtractionSchemaDialogProps) {
  const [fields, setFields] = useState<ExtractionField[]>([defaultField()]);

  useEffect(() => {
    if (open) {
      setFields(parseFields(value));
    }
  }, [open, value]);

  const updateField = (id: string, patch: Partial<ExtractionField>) => {
    setFields((prev) =>
      prev.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    );
  };

  const removeField = (id: string) => {
    setFields((prev) => {
      if (prev.length === 1) {
        return [defaultField()];
      }
      return prev.filter((f) => f.id !== id);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Extraction Review Form Builder</DialogTitle>
          <DialogDescription>
            Define fields and compile to JSON schema used by the review form.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-1">
          {fields.map((field, index) => (
            <div key={field.id} className="rounded-md border p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Field {index + 1}</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeField(field.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <Input
                  value={field.key}
                  placeholder="Field key (e.g. partNumber)"
                  onChange={(e) =>
                    updateField(field.id, { key: e.target.value })
                  }
                />
                <Input
                  value={field.label}
                  placeholder="Field label"
                  onChange={(e) =>
                    updateField(field.id, { label: e.target.value })
                  }
                />
                <Select
                  value={field.type}
                  onValueChange={(v) =>
                    updateField(field.id, { type: parseFieldType(v) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="string">Text</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                    <SelectItem value="boolean">Boolean</SelectItem>
                    <SelectItem value="array">List of Text</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={field.required ? "required" : "optional"}
                  onValueChange={(v) =>
                    updateField(field.id, { required: v === "required" })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="required">Required</SelectItem>
                    <SelectItem value="optional">Optional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea
                rows={2}
                value={field.description}
                placeholder="Field description"
                onChange={(e) =>
                  updateField(field.id, { description: e.target.value })
                }
              />
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => setFields((prev) => [...prev, defaultField()])}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Field
          </Button>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              onApply(buildSchema(fields));
              onOpenChange(false);
            }}
          >
            Apply JSON Schema
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
